import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, User, Loader } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from "@/components/ui/pagination";
import { Button } from "@/components/ui/button";
function NewsGridConsumer() {
  const {
    posts,
    loading,
    error,
    totalPages,
    currentPage,
    nextPage,
    prevPage,
    hasNext,
    hasPrev,
    setPage,
    refetch
  } = usePosts();
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 3; i++) pages.push(i);
        if (totalPages > 3) {
          pages.push("ellipsis");
          pages.push(totalPages);
        }
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("ellipsis");
        for (let i = totalPages - 2; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("ellipsis");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }
    return pages;
  };
  if (loading) {
    return <div className="flex min-h-[400px] w-full items-center justify-center">
        <Loader className="text-primary h-8 w-8 animate-spin" />
      </div>;
  }
  if (error) {
    return <div className="flex min-h-[200px] flex-col items-center justify-center gap-4 text-center">
        <p className="text-destructive">Error loading news: {error}</p>
        <Button onClick={refetch} variant="outline">
          Try Again
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex min-h-[200px] flex-col items-center justify-center text-center">
        <p className="text-muted-foreground text-lg">No news articles found.</p>
      </div>;
  }
  return <div className="space-y-12">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => <div key={post.id} data-index={i} className="group h-full">
            <Card className="h-full overflow-hidden border-none shadow-md transition-shadow duration-300 hover:shadow-xl">
              {}
              <div className="relative aspect-video w-full overflow-hidden bg-gray-100">
                {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-gray-200 text-gray-400">
                    <span className="text-sm">No Image</span>
                  </div>}
              </div>

              <CardHeader className="space-y-3 pt-6">
                {}
                <div className="flex flex-wrap gap-2">
                  {post.categories && post.categories.length > 0 ? post.categories.slice(0, 1).map(cat => <Badge key={cat.id} className="bg-ring hover:bg-ring/90 text-white rounded-sm px-2 py-1 text-xs font-semibold uppercase tracking-wider">
                        {cat.name}
                      </Badge>) : <Badge className="bg-ring hover:bg-ring/90 text-white rounded-sm px-2 py-1 text-xs font-semibold uppercase tracking-wider">
                      News
                    </Badge>}
                </div>

                {}
                <CardTitle className="text-primary line-clamp-2 text-xl font-bold leading-tight">
                  <Link to={post.link || "#"} className="hover:text-ring transition-colors">
                    {post.title}
                  </Link>
                </CardTitle>

                {}
                <div className="text-muted-foreground flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1">
                    <SafeHydrate><Calendar className="h-3 w-3" /></SafeHydrate>
                    {post.date ? new Date(post.date).toLocaleDateString() : "Recent"}
                  </span>
                  {post.author && <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {post.author.name}
                    </span>}
                </div>
              </CardHeader>

              <CardContent>
                <CardDescription className="line-clamp-3 text-sm">
                  {post.excerpt ? <span dangerouslySetInnerHTML={{
                __html: post.excerpt
              }} /> : "Read the full article to learn more about this topic and its impact on the construction industry."}
                </CardDescription>
              </CardContent>

              <CardFooter className="pb-6">
                <Link to={post.link || "#"} className="text-primary group/link flex items-center text-sm font-semibold transition-colors hover:text-ring">
                  Read More
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1" />
                </Link>
              </CardFooter>
            </Card>
          </div>)}
      </div>

      {}
      {totalPages > 1 && <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious href="#" onClick={e => {
            e.preventDefault();
            if (hasPrev) prevPage();
          }} className={!hasPrev ? "pointer-events-none opacity-50" : "cursor-pointer"} />
            </PaginationItem>

            {getPageNumbers().map((page, idx) => <PaginationItem key={idx}>
                {page === "ellipsis" ? <PaginationEllipsis /> : <PaginationLink href="#" isActive={currentPage === page} onClick={e => {
            e.preventDefault();
            setPage(page as number);
          }} className="cursor-pointer">
                    {page}
                  </PaginationLink>}
              </PaginationItem>)}

            <PaginationItem>
              <PaginationNext href="#" onClick={e => {
            e.preventDefault();
            if (hasNext) nextPage();
          }} className={!hasNext ? "pointer-events-none opacity-50" : "cursor-pointer"} />
            </PaginationItem>
          </PaginationContent>
        </Pagination>}
    </div>;
}
export default function NewsArchiveGrid() {
  const wp_query_params = {
    per_page: 6,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="news-archive-grid" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4">
        <div className="mb-12 text-center">
          <h2 className="text-primary mb-4 text-3xl font-bold tracking-tight md:text-4xl">
            Latest News & Updates
          </h2>
          <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
            Stay informed with the latest developments, research breakthroughs, and
            project updates from NUCETECH.
          </p>
        </div>

        <PostsProvider wp_query={{
        per_page: 6,
        orderby: "date",
        order: "desc"
      }} className="w-full">
          <NewsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}